import React, { useState } from 'react';
import { Box } from '@mui/material';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import SubscriptionBanner from '../SubscriptionBanner';

/**
 * Back-office layout: permanent sidebar (md+) or slide-over drawer (sm),
 * sticky top bar, scrollable main content, optional SubscriptionBanner at
 * the top of the content area.
 *
 * Pages opt-in by wrapping their root with <AppShell>:
 *
 *   const MyPage = () => (
 *     <AppShell>
 *       <PageHeader title="..." />
 *       ...content...
 *     </AppShell>
 *   );
 *
 * Props:
 *   children              — page body (typically starts with <PageHeader>)
 *   notificationCount     — passed through to TopBar
 *   maxWidth              — content max-width (default 'xl' = 1400, set false for fluid)
 *   showSubscriptionBanner — default true; set false on pages that already render their own
 */
const AppShell = ({
  children,
  notificationCount,
  maxWidth = 1400,
  showSubscriptionBanner = true,
  topBarRightSlot,
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleSidebar = () => setMobileOpen((v) => !v);
  const closeSidebar = () => setMobileOpen(false);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Sidebar
        variant="permanent"
        open
        onClose={closeSidebar}
      />
      <Sidebar
        variant="temporary"
        open={mobileOpen}
        onClose={closeSidebar}
      />

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          // No sidebar offset here: the permanent Drawer already reserves its
          // width in the flex row (width + flexShrink: 0), so main fills the
          // full remaining page width. Adding a margin double-offsets it.
        }}
      >
        <TopBar
          onMenuClick={toggleSidebar}
          notificationCount={notificationCount}
          rightSlot={topBarRightSlot}
        />

        <Box sx={{ flex: 1, px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 3.5 } }}>
          {showSubscriptionBanner && <SubscriptionBanner />}
          <Box
            sx={{
              maxWidth: maxWidth || 'none',
              marginInline: 'auto',
              width: '100%',
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
